import { useCallback, useRef } from "react"
import { usePopupStore } from "@/components/common/popup/store/usePopupStore"
import { useBottomSheetStore } from "@/components/common/bottomSheet/store/useBottomSheetStore"
import BookshelfAnalysisCompletePopup from "@/app/(tabs)/bookshelf/_components/Analysis/BookshelfAnalysisCompletePopup"
import BookshelfExtractConfirmation from "@/app/(tabs)/bookshelf/_components/Analysis/BookshelfExtractConfirmation"
import { ShelfAnalysisCompletePayload } from "@/utils/nativeEventBridge"
import { nativeBr } from "@/utils/nativeBr"
import { useRouter } from "next/navigation"

/**
 * 서재 분석 완료 이벤트를 처리하는 훅
 * - 중복 처리 방지
 * - 분석 성공 시 팝업 및 추출 확인 바텀시트 표시
 * - 기본적인 에러 처리
 */
export const useShelfAnalysisHandler = () => {
  const router = useRouter()
  const { openPopup, closePopup } = usePopupStore()
  const { openSheet } = useBottomSheetStore()

  // 기본적인 상태 관리
  const processedAnalysisIds = useRef(new Set<string>())
  const isProcessing = useRef(false)
  const debounceTimer = useRef<NodeJS.Timeout | null>(null)

  // 메모리 관리를 위한 최대 저장 개수
  const MAX_PROCESSED_IDS = 50

  // 메모리 정리 함수
  const cleanupProcessedIds = useCallback(() => {
    if (processedAnalysisIds.current.size > MAX_PROCESSED_IDS) {
      const idsArray = Array.from(processedAnalysisIds.current)
      const idsToKeep = idsArray.slice(-25) // 절반만 유지
      processedAnalysisIds.current = new Set(idsToKeep)
    }
  }, [])

  // 처리된 분석 ID 제거 함수 (API 실패 시 재시도를 위해)
  const removeProcessedId = useCallback((analysisId: string) => {
    processedAnalysisIds.current.delete(analysisId)
  }, [])

  // 서재 분석 완료 핸들러
  const handleAnalysisComplete = useCallback(
    (payload: ShelfAnalysisCompletePayload) => {
      const analysisId = payload.id

      // 중복 분석 ID 체크 (이벤트 도착 즉시)
      if (processedAnalysisIds.current.has(analysisId)) {
        console.log(`[ShelfAnalysisHandler] 이미 처리된 분석 ID: ${analysisId}`)
        return
      }

      // ID 즉시 등록 (중복 이벤트 차단)
      processedAnalysisIds.current.add(analysisId)
      console.log(`[ShelfAnalysisHandler] 분석 ID 등록: ${analysisId}`)

      // 디바운싱: 기존 타이머가 있으면 클리어하지 않고 그대로 진행
      // (동일 ID는 위에서 이미 차단되므로, 여기 도달하는 건 다른 ID의 이벤트)
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
        debounceTimer.current = null
      }

      // 현재 처리 중인지 체크
      if (isProcessing.current) {
        console.log(`[ShelfAnalysisHandler] 이미 처리 중인 이벤트가 있음, 대기`)
        return
      }

      // 디바운싱된 실행
      debounceTimer.current = setTimeout(() => {
        try {
          // 처리 상태 활성화
          isProcessing.current = true

          //analysisId로 네이티브 shelfPhotoFinalizeId 호출
          nativeBr
            .shelfPhotoFinalizeId({ analysisId: payload.id })
            .then(() => {
              console.log(
                `[ShelfAnalysisHandler] shelfPhotoFinalizeId 호출 완료: ${payload.id}`,
              )
            })
            .catch((error) => {
              console.error(
                `[ShelfAnalysisHandler] shelfPhotoFinalizeId 실패: ${payload.id}`,
                error,
              )
            })
            .finally(() => {
              // 처리 완료 후 상태 복구
              isProcessing.current = false
            })

          // 메인 로직 실행
          if (payload.status === "COMPLETED") {
            openPopup(
              <BookshelfAnalysisCompletePopup
                onConfirm={() => {
                  closePopup()
                  openSheet(
                    <BookshelfExtractConfirmation
                      analysisId={payload.id}
                      removeProcessedId={removeProcessedId}
                      captureDate={payload.date}
                      onComplete={() => {
                        console.log("책장 등록 완료")
                        router.refresh()
                      }}
                    />,
                    0,
                    false,
                  )
                }}
              />,
            )
          } else if (payload.status === "FAILED") {
            const errorMessage =
              payload.message || "알 수 없는 오류가 발생했습니다"
            console.error(`[ShelfAnalysisHandler] 분석 실패: ${errorMessage}`)

            // FAILED 상태에서는 팝업 없이 바로 시트만 열기
            // removeProcessedId를 undefined로 전달하여 API 실패 시 ID 제거 방지
            openPopup(
              <BookshelfAnalysisCompletePopup
                onConfirm={() => {
                  closePopup()
                  openSheet(
                    <BookshelfExtractConfirmation
                      analysisId={undefined}
                      removeProcessedId={undefined}
                      captureDate={payload.date}
                      onComplete={() => {
                        console.log("책장 분석 실패 처리 완료")
                      }}
                    />,
                    0,
                    false,
                  )
                }}
              />,
            )
          } else {
            console.warn("알 수 없는 분석 상태:", payload.status)
            console.error("알 수 없는 분석 결과 상태입니다")
          }

          // 메모리 정리
          cleanupProcessedIds()
        } catch (error) {
          console.error("이벤트 처리 중 오류:", error)

          // 에러 발생 시 상태 복구
          isProcessing.current = false
        }

        // 타이머 클리어
        debounceTimer.current = null
      }, 300) // 300ms 디바운스
    },
    [
      openPopup,
      closePopup,
      openSheet,
      removeProcessedId,
      cleanupProcessedIds,
      router,
    ],
  )

  return {
    handleAnalysisComplete,
    // API 실패 시 재시도를 위한 함수
    removeProcessedId,
  }
}
